import { ClockCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import Tooltip from "antd/es/tooltip";
import classNames from "clsx";
import { Button } from "components/styleguide";
import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatTimezoneDifference, getLocalTimezoneId, getTimezoneDifferenceInMinutes } from "utils/timezoneUtils";

import styles from "./styles.module.less";

interface EventLocationTimeInfoProps {
  className?: string;
  referenceDate?: Date | string | null;
  timezoneId?: string | null;
}

interface EventLocationTimeCopyProps extends EventLocationTimeInfoProps {
  compact?: boolean;
}

const getEventLocationLabel = (timezoneId?: string | null) => {
  return timezoneId ? `${timezoneId}-based event location` : "the event location timezone";
};

const getReferenceDate = (referenceDate?: Date | string | null) => {
  const date = referenceDate ? new Date(referenceDate) : new Date();

  if (Number.isNaN(date.getTime())) {
    return new Date();
  }

  date.setUTCHours(12, 0, 0, 0);

  return date;
};

const EventLocationTimeCopy: FC<EventLocationTimeCopyProps> = ({ compact = false, referenceDate, timezoneId }) => {
  const { t } = useTranslation();
  const timezoneInterpolation = { interpolation: { escapeValue: false } };
  const localTimezoneId = useMemo(() => getLocalTimezoneId(), []);
  const differenceReferenceDate = useMemo(() => getReferenceDate(referenceDate), [referenceDate]);
  const differenceInMinutes = useMemo(
    () => getTimezoneDifferenceInMinutes(timezoneId, localTimezoneId, differenceReferenceDate),
    [differenceReferenceDate, localTimezoneId, timezoneId],
  );
  const difference = formatTimezoneDifference(differenceInMinutes);
  const localTimezoneSuffix = localTimezoneId ? ` (${localTimezoneId})` : "";

  return (
    <div className={classNames(styles.copy, compact && styles.compactCopy)}>
      <div className={styles.title}>{t("Event Location Time (ELT)")}</div>
      <div className={styles.description}>
        {t("Enter times exactly as scheduled at {{eventLocation}} (e.g. 9:00 means 9:00 at the event).", {
          eventLocation: getEventLocationLabel(timezoneId),
          ...timezoneInterpolation,
        })}
      </div>
      <div className={styles.difference}>
        <strong>
          {t("Difference from your timezone{{localTimezone}}:", {
            localTimezone: localTimezoneSuffix,
            ...timezoneInterpolation,
          })}
        </strong>{" "}
        {difference}
        {differenceInMinutes !== null && ` (${t("No adjustments needed")})`}
      </div>
    </div>
  );
};

export const EventLocationTimeBanner: FC<EventLocationTimeInfoProps> = ({ className, referenceDate, timezoneId }) => {
  const { t } = useTranslation();

  return (
    <section className={classNames(styles.banner, className)} aria-label={t("Event Location Time guidance")}>
      <InfoCircleOutlined className={styles.bannerIcon} />
      <EventLocationTimeCopy referenceDate={referenceDate} timezoneId={timezoneId} />
    </section>
  );
};

export const EventLocationTimeHeader: FC<EventLocationTimeInfoProps> = ({ className, referenceDate, timezoneId }) => {
  const { t } = useTranslation();
  const label = timezoneId ?? t("Event timezone");

  return (
    <Tooltip
      overlayClassName={styles.tooltipOverlay}
      placement="bottomRight"
      title={<EventLocationTimeCopy compact referenceDate={referenceDate} timezoneId={timezoneId} />}
    >
      <Button
        aria-label={t("Event Location Time information")}
        className={classNames(styles.headerTrigger, styles.headerButton, className)}
        icon={<ClockCircleOutlined />}
        type="text"
      >
        <span className={styles.headerLabel}>
          {t("ELT")}: {label}
        </span>
      </Button>
    </Tooltip>
  );
};
